/**
 * Turing MCP API - for Smart Home Dashboard Template
 * Supports appId+apiKey (X-Api-Key) and OAuth (Authorization: Bearer)
 */

import { getCurrentApiUrl } from './environmentStore'

export type AuthCreds = { apiKey?: string; accessToken?: string; appId?: string }

function getApiBase(): string {
  return getCurrentApiUrl()
}

function buildAuthHeaders(auth: AuthCreds): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  if (auth.appId) h['Ultron-Cloud-Appid'] = auth.appId
  if (auth.accessToken) h['Authorization'] = `Bearer ${auth.accessToken}`
  else if (auth.apiKey) h['X-Api-Key'] = auth.apiKey
  return h
}

async function mcpCallWithAuth(
  tool: string,
  params: Record<string, unknown>,
  auth: AuthCreds
): Promise<{ success: boolean; data?: unknown; locations?: Location[]; groups?: Location[]; error?: string }> {
  const res = await fetch(`${getApiBase()}/mcp/${tool}`, {
    method: 'POST',
    headers: buildAuthHeaders(auth),
    body: JSON.stringify(params),
  })
  const json = await res.json()
  if (!res.ok) {
    const details = Array.isArray(json.details)
      ? json.details.map((item: unknown) => String(item)).join('; ')
      : (typeof json.details === 'string' ? json.details : '')
    const baseError = json.error || json.message || `HTTP ${res.status}`
    return {
      success: false,
      error: details ? `${baseError} (${details})` : baseError,
    }
  }
  if (json.success === false && json.error) return { success: false, error: json.error }
  return { success: true, ...json }
}

export type Location = {
  id?: string
  groupId?: string
  groupName?: string
  name?: string
  deviceCount?: number
}

export type Device = {
  sn: string
  deviceSN?: string
  name?: string
  displayName?: string
  nickname?: string
  deviceType?: string
  type?: string
  hwType?: string
  traits?: string[]
  capabilities?: Record<string, unknown>
  attributes?: Record<string, unknown>
  customControls?: Record<string, unknown>
  system?: Record<string, unknown>
  online?: boolean
}

export type DeviceStatus = {
  sn: string
  name?: string
  online?: boolean
  attributes?: Record<string, unknown>
  iotState?: Record<string, unknown>
}

async function mcpCall(
  tool: string,
  params: Record<string, unknown>,
  apiKey: string,
  appId: string
): Promise<{ success: boolean; data?: unknown; locations?: Location[]; groups?: Location[]; error?: string }> {
  return mcpCallWithAuth(tool, params, { apiKey, appId })
}

export async function queryLocationsWithAuth(auth: AuthCreds): Promise<Location[]> {
  const result = await mcpCallWithAuth('QueryLocations', {}, auth)
  if (!result.success) throw new Error(result.error || 'QueryLocations failed')
  const raw = (result as { data?: Location[] }).data ?? result.locations ?? result.groups ?? []
  const locs = Array.isArray(raw) ? raw : []
  return locs.map((g) => ({
    id: g.id ?? g.groupId ?? '',
    groupId: g.groupId ?? g.id ?? '',
    name: g.name ?? g.groupName ?? '',
    deviceCount: g.deviceCount,
  }))
}

export async function queryLocations(apiKey: string, appId: string): Promise<Location[]> {
  return queryLocationsWithAuth({ apiKey, appId })
}

function parseDevicesFromResult(result: { data?: unknown }): Device[] {
  const raw = result.data ?? []
  const groups = Array.isArray(raw) ? raw : []
  const devices: Device[] = []

  const normalizeToken = (value: unknown): string =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')

  const isUltronSensorEndpoint = (cap: { endpoint?: string; deviceSN?: string; type?: string }): boolean => {
    const endpointToken = normalizeToken(cap.endpoint)
    const snToken = normalizeToken(String(cap.deviceSN || '').split('#')[1] || '')
    const typeToken = normalizeToken(cap.type)
    return (
      endpointToken === 'ultron_sensor' ||
      snToken === 'ultron_sensor' ||
      typeToken === 'ultron_sensor'
    )
  }

  for (const g of groups) {
    const group = g as Record<string, unknown> & {
      system?: Record<string, unknown> & { baseSN?: string; deviceName?: string; deviceType?: string; online?: boolean }
      customControls?: Record<string, unknown>
      capabilities?: Array<{
        endpoint?: string
        deviceSN?: string
        type?: string
        hwType?: string
        nickname?: string
        traits?: string[]
        attributes?: Record<string, unknown>
      }>
    }
    if (group.deviceSN || group.sn) {
      const d = group
      const sn = String(d.deviceSN ?? d.sn ?? '')
      const nickname = ((d.nickname ?? d.displayName ?? '') as string).trim() || undefined
      const displayName = ((d.displayName ?? d.deviceName ?? '') as string).trim() || undefined
      const name = nickname ?? displayName ?? undefined
      devices.push({
        sn,
        deviceSN: sn,
        name,
        displayName,
        nickname,
        deviceType: (d.deviceType ?? d.type) as string | undefined,
        type: d.type as string | undefined,
        traits: d.traits as string[] | undefined,
        attributes: d.attributes as Record<string, unknown> | undefined,
        capabilities: (
          d.capabilities && typeof d.capabilities === 'object' && !Array.isArray(d.capabilities)
            ? d.capabilities as Record<string, unknown>
            : undefined
        ),
        customControls: (d.customControls ?? group.customControls) as Record<string, unknown> | undefined,
        system: (
          d.system && typeof d.system === 'object' && !Array.isArray(d.system)
            ? d.system as Record<string, unknown>
            : (
              group.system && typeof group.system === 'object' && !Array.isArray(group.system)
                ? group.system as Record<string, unknown>
                : undefined
            )
        ),
        online: d.online as boolean | undefined,
      })
      continue
    }
    const sys = group.system ?? {}
    const caps = Array.isArray(group.capabilities) ? group.capabilities : []
    const hasNonUltronSensorEndpoint = caps.some((cap) => !isUltronSensorEndpoint(cap))
    // One card per physical device (baseSN). Multi-endpoint devices (e.g. Water Dispenser
    // with endpoint #1 and #2) represent the same physical unit — emit only the first
    // non-sensor endpoint so the template shows exactly one card.
    let primaryCardEmitted = false
    for (const cap of caps) {
      if (hasNonUltronSensorEndpoint && isUltronSensorEndpoint(cap)) {
        // Hide synthetic internal sensor endpoint when a normal endpoint exists
        // (e.g. OUTLET1 + Ultron Sensor on the same baseSN).
        continue
      }
      if (primaryCardEmitted) continue
      primaryCardEmitted = true
      const deviceSN = cap.deviceSN ?? `${sys.baseSN ?? ''}#${cap.endpoint ?? '1'}`
      const nickname = (cap.nickname ?? '').trim() || undefined
      const displayName = (sys.deviceName ?? '').trim() || undefined
      const name = nickname ?? displayName ?? undefined
      devices.push({
        sn: deviceSN,
        deviceSN,
        name,
        displayName,
        nickname,
        deviceType: (sys.deviceType ?? cap.type) as string | undefined,
        type: cap.type as string | undefined,
        hwType: cap.hwType as string | undefined,
        traits: cap.traits,
        attributes: cap.attributes,
        capabilities: {
          traits: cap.traits ?? [],
          attributes: cap.attributes ?? {},
          type: cap.type,
          endpoint: cap.endpoint,
        },
        customControls: group.customControls,
        system: (
          group.system && typeof group.system === 'object' && !Array.isArray(group.system)
            ? group.system as Record<string, unknown>
            : undefined
        ),
        online: sys.online,
      })
    }
    if (caps.length === 0 && sys.baseSN) {
      const deviceSN = sys.baseSN
      const displayName = (sys.deviceName ?? '').trim() || undefined
      devices.push({
        sn: deviceSN,
        deviceSN,
        name: displayName,
        displayName,
        nickname: undefined,
        deviceType: sys.deviceType as string | undefined,
        type: undefined,
        traits: undefined,
        attributes: undefined,
        customControls: group.customControls as Record<string, unknown> | undefined,
        system: (
          group.system && typeof group.system === 'object' && !Array.isArray(group.system)
            ? group.system as Record<string, unknown>
            : undefined
        ),
        online: sys.online,
      })
    }
  }
  return devices
}

export async function queryDevicesWithAuth(
  auth: AuthCreds,
  locationId: string,
  _detailed = true
): Promise<Device[]> {
  const result = await mcpCallWithAuth('QueryDevices', { locationId }, auth)
  if (!result.success) throw new Error(result.error || 'QueryDevices failed')
  return parseDevicesFromResult(result)
}

export async function queryDevices(
  apiKey: string,
  appId: string,
  locationId: string,
  _detailed = true
): Promise<Device[]> {
  return queryDevicesWithAuth({ apiKey, appId }, locationId, _detailed)
}

export async function queryStatusWithAuth(
  auth: AuthCreds,
  locationId: string,
  devices?: string[]
): Promise<DeviceStatus[]> {
  const params: Record<string, unknown> = { locationId }
  if (devices && devices.length > 0) params.devices = devices
  const result = await mcpCallWithAuth('QueryStatus', params, auth)
  if (!result.success) throw new Error(result.error || 'QueryStatus failed')
  const raw = (result as { data?: unknown[] }).data ?? []
  const arr = Array.isArray(raw) ? raw : []
  const out: DeviceStatus[] = []
  for (const g of arr) {
    const group = g as { system?: { baseSN?: string; deviceName?: string; online?: boolean }; states?: { deviceSN?: string; values?: Record<string, { value?: unknown }> }[] }
    const sys = group.system ?? {}
    const states = group.states ?? []
    for (const st of states) {
      const deviceSN = st.deviceSN ?? sys.baseSN ?? ''
      const values = st.values ?? {}
      const attrs: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(values)) {
        attrs[k] = (v as { value?: unknown })?.value ?? v
      }
      // Canonicalize window-covering state: keep only openPercent.
      const rawOpenPercent =
        attrs.openPercent ??
        attrs.openClosePercent ??
        attrs.OpenClosePercent ??
        attrs.position
      if (rawOpenPercent != null) {
        attrs.openPercent = rawOpenPercent
      }
      delete attrs.openClosePercent
      delete attrs.OpenClosePercent
      delete attrs.position
      // Some battery-powered multi-endpoint devices expose battery on system-level only.
      // If endpoint values have no battery, inherit from group.system.battery for all endpoints.
      const hasBatteryInState =
        attrs.battery != null ||
        attrs.batteryLevel != null ||
        attrs.batteryPercent != null ||
        attrs.battery_percentage != null
      const systemBattery =
        (sys as { battery?: unknown }).battery
      if (!hasBatteryInState && typeof systemBattery === 'number' && Number.isFinite(systemBattery)) {
        attrs.battery = systemBattery
        attrs.batteryLevel = systemBattery
      }
      out.push({
        sn: deviceSN,
        name: (sys as { deviceName?: string }).deviceName as string | undefined,
        online: (sys as { online?: boolean }).online,
        attributes: attrs,
        iotState: attrs,
      })
    }
    if (states.length === 0 && sys.baseSN) {
      out.push({
        sn: (sys as { baseSN?: string }).baseSN ?? '',
        name: (sys as { deviceName?: string }).deviceName,
        online: (sys as { online?: boolean }).online,
        attributes: {},
        iotState: {},
      })
    }
  }
  return out
}

export async function queryStatus(
  apiKey: string,
  appId: string,
  locationId: string,
  devices?: string[]
): Promise<DeviceStatus[]> {
  return queryStatusWithAuth({ apiKey, appId }, locationId, devices)
}

export type Automation = {
  id: string
  name: string
  description?: string
  enabled: boolean
  type?: string
  conditions?: unknown
  schedule?: {
    enabled?: boolean
    timezone?: string
    daysOfWeek?: number[]
    timeRanges?: { start?: string; end?: string }[]
  }
  actions?: Array<{
    type: 'control' | 'scene' | 'notify'
    devices?: string[]
    command?: string
    params?: Record<string, unknown>
    sceneId?: string
    sceneIds?: string[]
    channels?: string[]
    message?: string
  }>
  triggerMode?: string
  trigger?: unknown
  delayedCheck?: unknown
  evaluationStrategy?: unknown
  groupId?: string
  createdAt?: string
  updatedAt?: string
  lastTriggered?: string
  triggerCount?: number
}

export type SceneActionParams = {
  brightness?: number
  temperature?: number
  mode?: string
  fanSpeed?: string
  openPercent?: number
  colorTemp?: number
  [key: string]: unknown
}

export type SceneDeviceCommand = {
  sn: string
  command: string
  params?: SceneActionParams
}

export type ConfigureSceneParams = {
  action: 'query' | 'create' | 'update' | 'delete' | 'run'
  id?: string
  sceneId?: string
  name?: string
  icon?: string
  groupId?: string
  includeDetails?: boolean
  enabled?: boolean
  devices?: SceneDeviceCommand[]
}

export type ConfigureSceneResult = {
  success: boolean
  sceneId?: string
  id?: string
  data?: unknown
  result?: unknown
  error?: string
  [key: string]: unknown
}

export async function queryAutomationsWithAuth(
  auth: AuthCreds,
  locationId?: string
): Promise<Automation[]> {
  const params: Record<string, unknown> = {}
  if (locationId) params.locationId = locationId
  const result = await mcpCallWithAuth('QueryAutomations', params, auth)
  if (!result.success) throw new Error(result.error || 'QueryAutomations failed')
  const res = result as { result?: { automations?: Automation[] } }
  const automations = res.result?.automations ?? []
  return Array.isArray(automations) ? automations : []
}

export async function configureSceneWithAuth(
  auth: AuthCreds,
  params: ConfigureSceneParams
): Promise<ConfigureSceneResult> {
  const result = await mcpCallWithAuth('ConfigureScene', params as unknown as Record<string, unknown>, auth)
  if (!result.success) {
    throw new Error(result.error || 'ConfigureScene failed')
  }
  return result as unknown as ConfigureSceneResult
}

export async function queryScenesWithAuth(
  auth: AuthCreds,
  includeDetails = false,
  groupId?: string
): Promise<ConfigureSceneResult> {
  const result = await configureSceneWithAuth(auth, {
    action: 'query',
    includeDetails,
    ...(groupId ? { groupId } : {}),
  })
  const record = (result && typeof result === 'object') ? (result as Record<string, unknown>) : {}
  const scenes = Array.isArray(record.scenes)
    ? (record.scenes as unknown[])
    : (Array.isArray(record.scene) ? (record.scene as unknown[]) : [])

  if (scenes.length > 0) {
    return {
      ...result,
      scenes,
      count: Number(record.count ?? scenes.length),
    }
  }

  // Workaround for staging MCP scene query returning empty while UltronCloud has scene[] payload.
  // Try direct UltronCloud v4 GetGroupScenes with the same credentials.
  try {
    let targetGroupIds: string[] = []
    if (groupId) {
      targetGroupIds = [groupId]
    } else {
      // Without a selected location, do not merge all locations into one scene list.
      return {
        ...result,
        scenes: [],
        count: Number(record.count ?? 0),
      }
    }

    const grouped = await Promise.all(
      targetGroupIds.map(async (gid) => {
        const res = await fetch('https://api.ultroncloud.com/usr/v4/GetGroupScenes', {
          method: 'POST',
          headers: buildAuthHeaders(auth),
          body: JSON.stringify({ groupId: gid }),
        })
        if (!res.ok) return []
        const json = await res.json() as Record<string, unknown>
        const rows = Array.isArray(json.scene)
          ? (json.scene as Array<Record<string, unknown>>)
          : (Array.isArray(json.scenes) ? (json.scenes as Array<Record<string, unknown>>) : [])
        return rows.map((row) => ({
          ...row,
          GroupId: row.GroupId ?? gid,
          groupId: row.groupId ?? row.GroupId ?? gid,
        }))
      })
    )
    const fallbackScenes = grouped.flat()
    if (fallbackScenes.length > 0) {
      return {
        ...result,
        scenes: fallbackScenes,
        count: fallbackScenes.length,
        source: 'ultroncloud-direct-fallback',
      }
    }
  } catch {
    // Keep original result when fallback is unavailable.
  }

  return {
    ...result,
    scenes: [],
    count: Number(record.count ?? 0),
  }
}

export async function runSceneWithAuth(
  auth: AuthCreds,
  sceneId: string,
  groupId?: string
): Promise<ConfigureSceneResult> {
  return configureSceneWithAuth(auth, { action: 'run', sceneId, ...(groupId ? { groupId } : {}) })
}

export async function deleteSceneWithAuth(
  auth: AuthCreds,
  sceneId: string,
  groupId?: string
): Promise<ConfigureSceneResult> {
  return configureSceneWithAuth(auth, { action: 'delete', sceneId, ...(groupId ? { groupId } : {}) })
}

export async function queryAutomations(
  apiKey: string,
  appId: string,
  locationId?: string
): Promise<Automation[]> {
  return queryAutomationsWithAuth({ apiKey, appId }, locationId)
}

export type ConfigureAutomationParams = {
  action?: 'create' | 'update' | 'delete'
  id?: string
  name: string
  description?: string
  enabled?: boolean
  locationId?: string
  groupId?: string
  conditions: unknown
  schedule?: {
    enabled?: boolean
    timezone?: string
    daysOfWeek?: number[]
    timeRanges?: { start?: string; end?: string }[]
  }
  actions: Array<{
    type: 'control' | 'scene' | 'notify'
    devices?: string[]
    command?: string
    params?: Record<string, unknown>
    sceneId?: string
    sceneIds?: string[]
    channels?: string[]
    message?: string
  }>
  triggerMode?: string
  trigger?: unknown
  delayedCheck?: unknown
  evaluationStrategy?: unknown
}

export async function configureAutomationWithAuth(
  auth: AuthCreds,
  params: ConfigureAutomationParams
): Promise<{ success: boolean; rule?: Automation; error?: string }> {
  const result = await mcpCallWithAuth('ConfigureAutomation', params, auth)
  if (!result.success) throw new Error(result.error || 'ConfigureAutomation failed')
  const res = result as { result?: { rule?: Automation } }
  return { success: true, rule: res.result?.rule }
}

export async function configureAutomation(
  apiKey: string,
  appId: string,
  params: ConfigureAutomationParams
): Promise<{ success: boolean; rule?: Automation; error?: string }> {
  return configureAutomationWithAuth({ apiKey, appId }, params)
}

export async function configureDeviceNicknameWithAuth(
  auth: AuthCreds,
  deviceSN: string,
  nickname: string
): Promise<{ success: boolean; error?: string }> {
  const normalizedDeviceSN = String(deviceSN || '').trim()
  const normalizedNickname = String(nickname || '').trim()
  if (!normalizedDeviceSN) return { success: false, error: 'deviceSN is required' }
  if (!normalizedNickname) return { success: false, error: 'nickname is required' }
  const result = await mcpCallWithAuth('ConfigureDevice', { deviceSN: normalizedDeviceSN, nickname: normalizedNickname }, auth)
  if (!result.success) return { success: false, error: result.error || 'ConfigureDevice failed' }
  return { success: true }
}

export async function executeCommandWithAuth(
  auth: AuthCreds,
  devices: string[],
  command: string,
  params?: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
  const body: Record<string, unknown> = { devices, command }
  if (params) body.params = params
  const result = await mcpCallWithAuth('ExecuteCommand', body, auth)
  if (!result.success) return { success: false, error: result.error }
  return { success: true }
}

export async function executeCommand(
  apiKey: string,
  appId: string,
  devices: string[],
  command: string,
  params?: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
  return executeCommandWithAuth({ apiKey, appId }, devices, command, params)
}

export type FirmwareCheckParams = {
  sn?: string
  groupId?: string
  getFirmwareURL?: boolean
}

export type FirmwareCheckResult = {
  success: boolean
  hasUpdate: boolean
  latestVersion?: string
  error?: string
}

function normalizeBaseSN(value: string): string {
  return String(value || '').trim().split('#')[0] || ''
}

function parseLatestFirmwareVersion(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined
  const obj = payload as Record<string, unknown>
  const direct = String(
    obj.Name ?? obj.name ?? obj.firmwareVersion ?? obj.fwVersion ?? obj.version ?? obj.Version ?? ''
  ).trim()
  if (direct) return direct
  const major = Number(obj.Major ?? obj.major)
  const minor = Number(obj.Minor ?? obj.minor)
  const patch = Number(obj.Patch ?? obj.patch)
  if (Number.isFinite(major) && Number.isFinite(minor) && Number.isFinite(patch)) {
    return `${major}.${minor}.${patch}`
  }
  return undefined
}

export async function checkFirmwareUpdatesWithAuth(
  auth: AuthCreds,
  params: FirmwareCheckParams
): Promise<FirmwareCheckResult> {
  const sn = String(params.sn || '').trim()
  const groupId = String(params.groupId || '').trim()
  if (!sn && !groupId) {
    return { success: false, hasUpdate: false, error: 'sn or groupId is required' }
  }

  try {
    const res = await fetch('https://api.ultroncloud.com/usr/v5/CheckFirmware', {
      method: 'POST',
      headers: buildAuthHeaders(auth),
      body: JSON.stringify({
        ...(sn ? { sn } : {}),
        ...(groupId ? { groupId } : {}),
        getFirmwareURL: Boolean(params.getFirmwareURL ?? false),
      }),
    })
    const json = await res.json() as Record<string, unknown>
    if (!res.ok) {
      return {
        success: false,
        hasUpdate: false,
        error: String(json.error || json.message || `HTTP ${res.status}`),
      }
    }

    const firmwareMap = (json.newFirmwareVersion && typeof json.newFirmwareVersion === 'object' && !Array.isArray(json.newFirmwareVersion))
      ? (json.newFirmwareVersion as Record<string, unknown>)
      : {}
    const entries = Object.entries(firmwareMap)
    if (entries.length === 0) {
      return { success: true, hasUpdate: false }
    }

    let targetPayload: unknown
    if (sn) {
      const targetBaseSN = normalizeBaseSN(sn)
      targetPayload = entries.find(([key]) => normalizeBaseSN(key) === targetBaseSN)?.[1]
    }
    if (!targetPayload) {
      targetPayload = entries[0]?.[1]
    }

    const latestVersion = parseLatestFirmwareVersion(targetPayload)
    return { success: true, hasUpdate: true, ...(latestVersion ? { latestVersion } : {}) }
  } catch (error) {
    return {
      success: false,
      hasUpdate: false,
      error: error instanceof Error ? error.message : 'Check firmware failed',
    }
  }
}

// ── GetUserInfo (OAuth only) ──────────────────────────────────────────────
export interface UserInfo {
  uid?: string
  email?: string
  displayName?: string
  photoURL?: string
  appId?: string
  orgInfo?: {
    isRoot?: boolean
    isAdmin?: boolean
    roles?: string[]
    [key: string]: unknown
  }
  [key: string]: unknown
}

export async function queryUserInfoWithAuth(auth: AuthCreds): Promise<UserInfo | null> {
  if (!('accessToken' in auth)) return null
  try {
    const res = await mcpCallWithAuth('GetUserInfo', {}, auth)
    if (!res.success) return null
    const r = (res as Record<string, unknown>).result as Record<string, unknown> | undefined
    const userInfo = (r?.userInfo ?? r) as UserInfo | undefined
    return userInfo || null
  } catch {
    return null
  }
}
